import BaseApi, { ApiResponse } from './base';

export interface RoleDistribution {
  learner: number;
  creator: number;
  admin: number;
}

export interface DepartmentCount {
  departmentId: string;
  departmentName: string;
  count: number;
}

export interface DailyActiveUsers {
  date: string;
  count: number;
}

export interface UsersSummary {
  total: number;
  byRole: RoleDistribution;
  byDepartment: DepartmentCount[];
  activeLearners: number;
  engagementRate: number;
  dailyActiveUsers: DailyActiveUsers[];
}

export interface TopEnrolledCourse {
  courseId: string;
  name: string;
  enrollments: number;
}

export interface CategoryCount {
  categoryId: string;
  categoryName: string;
  count: number;
}

export interface CoursesSummary {
  total: number;
  published: number;
  draft: number;
  recommended: number;
  totalEnrollments: number;
  uniqueUsersEnrolled: number;
  completionRate: number;
  inProgressUsers: number;
  avgTimeToComplete: number | null;
  topEnrolled: TopEnrolledCourse[];
  byCategory: CategoryCount[];
  byDepartment: DepartmentCount[];
}

export interface DifficultyDistribution {
  beginner: number;
  intermediate: number;
  advanced: number;
}

export interface TopViewedGuide {
  guideId: string;
  title: string;
  viewCount: number;
}

export interface LanguageDistribution {
  language: string;
  count: number;
}

export interface GuidesSummary {
  total: number;
  published: number;
  draft: number;
  byDifficulty: DifficultyDistribution;
  completionRate: number;
  avgQuizScore: number | null;
  chatSessionsTotal: number;
  topViewed: TopViewedGuide[];
  byCategory: CategoryCount[];
  languageDistribution: LanguageDistribution[];
}

export interface CompletionFunnel {
  enrolled: number;
  started: number;
  inProgress: number;
  completed: number;
}

export interface ContentUtilization {
  overallRate: number;
  courseInteractionRate: number;
  guideInteractionRate: number;
  uniqueUsersWithContent: number;
  totalUsers: number;
}

export interface CrossCuttingSummary {
  contentUtilization: ContentUtilization;
  completionFunnel: CompletionFunnel;
}

export interface AnalyticsSummary {
  users: UsersSummary;
  courses: CoursesSummary;
  guides: GuidesSummary;
  crossCutting: CrossCuttingSummary;
  cachedAt: string;
}

class AnalyticsApi extends BaseApi {
  async getSummary(): Promise<ApiResponse<AnalyticsSummary>> {
    return this.get<AnalyticsSummary>('/analytics/summary');
  }

  async invalidateCache(): Promise<ApiResponse<void>> {
    return this.delete<void>('/analytics/summary/cache');
  }
}

export const analyticsApi = new AnalyticsApi();

